import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MaterialModule } from '@app/shared/material/material.module';
import { QrCodeComponent } from 'ng-qrcode';
import { SignaturePadComponent } from '@almothafar/angular-signature-pad';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { TranslateModule } from '@ngx-translate/core';

import { ModalDialogComponent } from './components/modal-dialog/modal-dialog.component';
import { TableFilterComponent } from './components/table/table-filter/table-filter.component';
import { SignatureFieldComponent } from './components/signature-field/signature-field.component';
import { QuestionDialogComponent } from './components/question-dialog/question-dialog.component';
import { CarrierExchangeComponent } from './components/carrier-exchange/carrier-exchange.component';
import { SendWalletInviteComponent } from '@app/shared/components/send-wallet-invite/send-wallet-invite.component';
import { DeliveryNotesComponent } from './components/delivery-notes/delivery-notes.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';

import { TranslateDiscrepancyReasonsPipe } from './pipes/translate-discrepancy-reasons.pipe';
import { DeliveryNoteNumberPipe } from './pipes/delivery-note-number.pipe';
import { TranslateDiscrepancyUnitsPipe } from '@app/shared/pipes/translate-discrepancy-units.pipe';
import { FilterByNamePipe } from './pipes/filter-by-name.pipe';
import { FilterByDfnPipe } from './pipes/filter-by-dfn.pipe';

import { OutlinedDirective } from './directives/outlined.directive';
import { BolderEntryDirective } from '@app/shared/directives/bolder-entry.directive';
import { DragDropDirective } from './directives/drag-drop.directive';
import { SelectDisplayedColsDialogComponent } from './components/select-displayed-cols-dialog/select-displayed-cols-dialog.component';
import { LoadcarrierSwapComponent } from '@app/consignor/pages/create-new-folder/loadcarrier-swap/loadcarrier-swap.component';
import { PersistentTableSortDirective } from './directives/persistent-table-sort.directive';
import { IncludesPipe } from './pipes/includes.pipe';
import { UserIdleComponent } from './components/user-idle/user-idle.component';
import { DownloadNoteButtonComponent } from './components/download-note-button/download-note-button.component';
import { TourDeliveryNotesComponent } from './components/delivery-notes/tour-delivery-notes/tour-delivery-notes.component';
import { SendTourWalletInviteComponent } from './components/send-wallet-invite/send-tour-wallet-invite/send-tour-wallet-invite.component';
import { AlertDialogComponent } from './components/alert-dialog/alert-dialog.component';
import { ChangeNameDialogComponent } from './components/change-name-dialog/change-name-dialog.component';

import { FileInputAccessor } from './directives/file-input-accessor.directive';
import { UsersComponent } from './components/users/users.component';
import { FilterUserSitesComponent } from './components/filter-user-sites/filter-user-sites.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { ShowPdfDialogComponent } from './components/show-pdf-dialog/show-pdf-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { AttachmentCounterComponent } from './components/attachment-counter/attachment-counter.component';
import { FeedbackComponent } from './components/feedback/feedback.component';
import { UserIdlePopupComponent } from './components/user-idle/user-idle-popup.component';
import { ViewBundleDialogComponent } from './components/view-bundle-dialog/view-bundle-dialog.component';
import { TermsDialogComponent } from './components/terms-dialog/terms-dialog.component';
import { ViewTourDialogComponent } from './components/view-tour-dialog/view-tour-dialog.component';
import { CustomTooltipComponent } from './components/custom-tooltip/custom-tooltip.component';
import { OneButtonDialogComponent } from './components/one-button-dialog/one-button-dialog.component';
import { OverviewButtonsComponent } from './components/overview-buttons/overview-buttons.component';
import { ResizableComponent } from './components/resizable/resizable.component';
import { ArticlesDiscrepanciesDialogComponent } from './components/articles-discrepancies-dialog/articles-discrepancies-dialog.component';
import { LoadCarrierFormComponent } from './components/articles-discrepancies-dialog/load-carrier-form/load-carrier-form.component';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { IdleDialogComponent } from './components/idle-dialog/idle-dialog.component';
import { NgxBarcode6 } from 'ngx-barcode6';
import { ActiveEcmrComponent } from '@app/shared/components/ecmr/active-ecmr/active-ecmr.component';
import { TemplatesEcmrComponent } from '@app/shared/components/ecmr/templates-ecmr/templates-ecmr.component';
import { ArchiveEcmrComponent } from '@app/shared/components/ecmr/archive-ecmr/archive-ecmr.component';
import { EcmrStatusComponent } from '@app/shared/components/ecmr/subcomponents/ecmr-status/ecmr-status.component';
import { LoadFromTemplateDialogComponent } from '@app/shared/components/ecmr/dialogs/load-from-template-dialog/load-from-template-dialog.component';
import { TemplateNameDialogComponent } from '@app/shared/components/ecmr/dialogs/template-name-dialog/template-name-dialog.component';
import { SignaturePadDialogComponent } from '@app/shared/components/ecmr/dialogs/signature-pad-dialog/signature-pad-dialog.component';
import { EcmrConfirmationDialogComponent } from '@app/shared/components/ecmr/dialogs/ecmr-confirmation-dialog/ecmr-confirmation-dialog.component';
import { EcmrDetailDialogComponent } from '@app/shared/components/ecmr/dialogs/ecmr-detail-dialog/ecmr-detail-dialog.component';
import { EcmrDisplayInformationFieldComponent } from '@app/shared/components/ecmr/subcomponents/ecmr-display-information-field/ecmr-display-information-field.component';
import { EcmrShareDialogComponent } from '@app/shared/components/ecmr/dialogs/ecmr-share-dialog/ecmr-share-dialog.component';
import { EcmrEditorComponent } from '@app/shared/components/ecmr/ecmr-editor/ecmr-editor.component';
import { EcmrHistoryLogsComponent } from '@app/shared/components/ecmr/ecmr-history-logs/ecmr-history-logs.component';
import { DynamicDisableControlDirective } from '@app/shared/components/ecmr/dynamic-disable-control.directive';
import { FilterSiteByNamePipe } from '@app/shared/pipes/filter-site-by-name.pipe';
import { EcmrImportDialogComponent } from '@app/shared/components/ecmr/dialogs/ecmr-import-dialog/ecmr-import-dialog.component';
import { ChainSelectionDialogComponent } from './components/chain-selection-dialog/chain-selection-dialog.component';
import { MaxFileSizeDirective } from './directives/max-file-size.directive';
import { PaginatedTableFilterComponent } from './components/table/paginated-table-filter/paginated-table-filter.component';
import { GenericTableComponent } from './components/generic-table/generic-table.component';
import { ConfigurableViewComponent } from './components/configurable-view/configurable-view.component';
import { TablePaginationComponent } from './components/table-pagination/table-pagination.component';
import { SaveWarningDialogComponent } from '@app/shared/components/ecmr/dialogs/save-warning-dialog/save-warning-dialog.component';
import { EcmrDocumentsComponent } from '@app/shared/components/ecmr/ecmr-editor/ecmr-documents/ecmr-documents.component';
import { FeatureHintComponent } from '@app/shared/components/feature-hints/feature-hint/feature-hint.component';
import {
    FeatureHintTooltipComponent
} from '@app/shared/components/feature-hints/feature-hint-tooltip/feature-hint-tooltip.component';

// Standalone components/directives/pipes — go in imports/exports
const standaloneDeclarations = [
    BolderEntryDirective,
    ModalDialogComponent,
    TableFilterComponent,
    SignatureFieldComponent,
    DeliveryNoteNumberPipe,
    TranslateDiscrepancyReasonsPipe,
    TranslateDiscrepancyUnitsPipe,
    QuestionDialogComponent,
    FilterByDfnPipe,
    CarrierExchangeComponent,
    SendWalletInviteComponent,
    OutlinedDirective,
    DeliveryNotesComponent,
    FilterByNamePipe,
    ConfirmationDialogComponent,
    DragDropDirective,
    SelectDisplayedColsDialogComponent,
    LoadcarrierSwapComponent,
    PersistentTableSortDirective,
    IncludesPipe,
    UserIdleComponent,
    DownloadNoteButtonComponent,
    TourDeliveryNotesComponent,
    SendTourWalletInviteComponent,
    AlertDialogComponent,
    ChangeNameDialogComponent,
    FileInputAccessor,
    UsersComponent,
    FilterUserSitesComponent,
    PageNotFoundComponent,
    ShowPdfDialogComponent,
    AttachmentCounterComponent,
    FeedbackComponent,
    UserIdlePopupComponent,
    OverviewButtonsComponent,
    IdleDialogComponent,
    MaxFileSizeDirective,
    PaginatedTableFilterComponent,
    GenericTableComponent,
    ConfigurableViewComponent,
    TermsDialogComponent,
    TablePaginationComponent,
    ViewBundleDialogComponent,
    ViewTourDialogComponent,
    CustomTooltipComponent,
    OneButtonDialogComponent,
    DynamicDisableControlDirective,
    FeatureHintComponent,
    FeatureHintTooltipComponent,
    // Migrated to standalone (formerly declared) — now imported/exported:
    FilterSiteByNamePipe,
    ResizableComponent,
    ActiveEcmrComponent,
    TemplatesEcmrComponent,
    ArchiveEcmrComponent,
    EcmrStatusComponent,
    LoadFromTemplateDialogComponent,
    TemplateNameDialogComponent,
    SignaturePadDialogComponent,
    EcmrConfirmationDialogComponent,
    EcmrDetailDialogComponent,
    EcmrDisplayInformationFieldComponent,
    EcmrShareDialogComponent,
    EcmrEditorComponent,
    EcmrHistoryLogsComponent,
    EcmrImportDialogComponent,
    ChainSelectionDialogComponent,
    SaveWarningDialogComponent,
    EcmrDocumentsComponent,
    ArticlesDiscrepanciesDialogComponent,
    LoadCarrierFormComponent,
];
const imports = [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    SignaturePadComponent,
    FormsModule,
    QrCodeComponent,
    NgxExtendedPdfViewerModule,
    TranslateModule,
    MatDialogModule,
    ClipboardModule,
    NgxBarcode6,
];
@NgModule({
    imports: [...imports, ...standaloneDeclarations],
    exports: [...imports, ...standaloneDeclarations],
})
export class SharedModule {}
